import React, { Suspense, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import TopBar from "./TopBar";
import { Toaster } from "sonner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useOnlineStatus } from "@/utils/connectionUtils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { WifiOff, Menu } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useResponsive } from "@/hooks/useResponsive";
import { cn } from "@/lib/utils";

interface AdminLayoutProps {
  children?: React.ReactNode;
  hideTopBar?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  children,
  hideTopBar = false,
}) => {
  console.log("AdminLayout: render start");
  const isOnline = useOnlineStatus();
  const { user, userRole, isAdmin, ready } = useAuth();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Add debug logging with component info and auth details to better track what's rendering
  console.log("AdminLayout rendering with:", { 
    childrenCount: React.Children.count(children),
    childrenTypes: React.Children.map(children, child => 
      child && typeof child === 'object' && 'type' in child 
        ? (child.type as { displayName?: string; name?: string })?.displayName || (child.type as { displayName?: string; name?: string })?.name || 'Unknown' 
        : typeof child
    ),
    user: !!user,
    userRole,
    isAdmin,
    isBizOwner: userRole === 'bizadmin' || userRole === 'owner'
  });
  
  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }
  
  const content = children || <Outlet />;
  
  return (
    <div className={cn(
      "flex min-h-screen",
      "bg-gradient-to-br from-slate-50 via-white to-blue-50"
    )}>
      {/* Responsive Sidebar */}
      {isDesktop ? (
        // Desktop: Always visible sidebar
        <div className="fixed left-0 top-0 z-30">
          <AdminSidebar 
            isOpen={true}
            onToggle={toggleSidebar}
            onClose={closeSidebar}
          />
        </div>
      ) : (
        // Mobile/Tablet: Overlay sidebar
        <AdminSidebar 
          isOpen={sidebarOpen}
          onToggle={toggleSidebar}
          onClose={closeSidebar}
        />
      )}

      {/* Main content area with proper responsive margins */}
      <div className={cn(
        "flex flex-col min-h-screen transition-all duration-300",
        // Desktop: Account for fixed sidebar
        isDesktop && "ml-52",
        // Mobile/Tablet: Full width
        (isMobile || isTablet) && "w-full"
      )}>
        {/* Enhanced Top bar with responsive design */}
        {!hideTopBar && (
          <div className="h-14 md:h-16 bg-white border-b border-gray-200/60">
            <div className="relative flex items-center h-full px-4 md:px-6">
              {/* Mobile hamburger menu */}
              {(isMobile || isTablet) && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="mr-3 rounded-xl"
                  onClick={toggleSidebar}
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle Menu</span>
                </Button>
              )}
              
              <div className="flex-1">
                <TopBar 
                  hideMenuButton={true}
                  onGettingStartedClick={() => navigate('/admin/getting-started')} 
                />
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 w-full overflow-x-hidden">
          {!isOnline && (
            <Alert className="mb-4 mx-6 mt-6 bg-amber-50 border-amber-300 rounded-xl">
              <WifiOff className="h-4 w-4 text-amber-800" />
              <AlertDescription className="text-amber-800 font-medium">
                You are currently offline. Some features may be unavailable.
              </AlertDescription>
            </Alert>
          )}
          <ErrorBoundary componentName="MainContent">
            <Suspense fallback={(function(){console.log("AdminLayout: Suspense fallback spinner shown"); return <div className="flex justify-center items-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>;})()}>
              {content}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
